import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostBinding,
  inject,
  Renderer2,
  signal,
  viewChild,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Data, NavigationEnd, Router } from '@angular/router';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { Observable, filter, noop, take } from 'rxjs';
import { Store } from '@ngxs/store';
import { TreoMediaWatcherService } from 'tsui/@treo';
import { TreoNavigationService, TreoNavigationItem } from 'tsui/@treo';
import { guid } from 'app/shared/Utils/common.utils';
import { AccountService, AuthService } from 'app/core';
import { Account } from 'app/core/models/account.types';
import { UserService } from 'app/layout/common/user/user.service';
import { Project, Pipeline, Property } from 'app/modules/projects/projects.types';
import { Contact } from 'app/modules/contacts/contacts.types';
import { DashboardService } from 'app/modules/dashboard/dashboard.service';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { FeatureFlag } from 'app/core/enums/feature-flag';
import { ObjectsManagerActions } from 'app/modules/settings/objects-manager/state/actions';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { CustomObject } from 'app/shared/modules/custom-objects/custom-objects.types';
import { FloatingEthanComponent } from 'app/shared/components/organism/floating-ethan/floating-ethan.component';
import { Company } from 'app/modules/companies/companies.types';
import { Module, NewPermission, ReservedRole } from 'app/core/enums';
import { User } from 'app/core/models';
import { UserState } from '@state/user/state';
import { AccountState } from '@state/account/state';
import { RelatedToType } from '@shared/enums';
import { calculateDialogPosition } from '@shared/components/organism/floating-ethan/floating-ethan-position.helper';
import { PermissionUtils } from '@core/utils/permission.utils';
import { EthanState } from '@state/ethan/ethan.state';
import { SetEthanButtonVisible, SetEthanChatOpen } from '@state/ethan/ethan.action';
import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { Dictionary, TsOverlayComponent } from 'tsui';
import { getObjectIconMap } from './functions';

@Component({
  selector: 'modern-layout',
  templateUrl: './modern.component.html',
  styleUrls: ['./modern.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ModernLayoutComponent {
  data: Data = {};
  isScreenSmall: boolean;
  menuItems: TreoNavigationItem[] = [];
  chatIsOpen = false;
  chatInstance: MatDialogRef<any, any>;
  account: Account;
  pipelines: Pipeline[];
  projects: Project[];
  contacts: Contact[];
  companies: Company[];
  favoriteRecords: any[];
  customObjects: CustomObject[] = [];
  isTaskCollaborator: boolean;
  fromAccounts = false;
  isDragging = false;

  private hasMoved = false;
  private readonly CHAT_POSITION_KEY = 'chat-button-position';
  private readonly FLOATING_ETHAN_KEY = 'floating-ethan-visible';
  private chatButtonPosition: { x: number; y: number };
  private DEAL_MENU_ITEM_TITLE = 'Deals';
  private PROPERTY_MENU_ITEM_TITLE = 'Properties';

  isFloatingEthanVisible: boolean;
  ethanButtonVisible$: Observable<boolean> = this.store.select(EthanState.ethanButtonVisible);

  @HostBinding('class.fixed-header') fixedHeader: boolean;
  @HostBinding('class.fixed-footer') fixedFooter: boolean;

  @ViewChild('mainNavigation') mainNavigation: ElementRef;
  @ViewChild('chatButtonWrapper', { static: false }) chatButtonRef: ElementRef;

  customObjects$: Observable<CustomObject[]> = this.store.select(ObjectsManagerState.getForMenu);
  ethanChatOpen$: Observable<boolean> = this.store.select(EthanState.ethanChatOpen);
  isNewNavigationOn = this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_NAVIGATION));

  readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly treoMediaWatcherService: TreoMediaWatcherService,
    private readonly treoNavigationService: TreoNavigationService,
    private readonly accountService: AccountService,
    private readonly userService: UserService,
    private readonly dashboardService: DashboardService,
    private readonly projectsService: ProjectsService,
    private readonly authService: AuthService,
    private readonly store: Store,
    private readonly matDialog: MatDialog,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly renderer: Renderer2,
    private readonly _router: Router,
  ) {
    this.fixedHeader = false;
    this.fixedFooter = false;

    this.setInitialPositionForChatButton();

    // Update wrapper position on initialization
    this.updateChatButtonPosition();

    this.accountService.currentAccount.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((accountData) => {
      this.account = accountData;
      this.dashboardService.accountId = this.account.id;
    });

    this.objectIconMap = getObjectIconMap(this.isNewNavigationOn);
  }

  objectIconMap: Dictionary<string>;

  public setInitialPositionForChatButton(): void {
    // Initialize chat button position from local storage or default
    const savedPosition = localStorage.getItem(this.CHAT_POSITION_KEY);
    this.chatButtonPosition = savedPosition ? JSON.parse(savedPosition) : { x: 24, y: window.innerHeight - 104 };

    // Initialize floating Ethan visibility from local storage or default to true
    const storedVisibility = localStorage.getItem(this.FLOATING_ETHAN_KEY);
    this.isFloatingEthanVisible = storedVisibility === null || storedVisibility !== 'false';
  }

  get canAccessEthan(): boolean {
    if (this.account) {
      const currentUrl = this.activatedRoute.snapshot.firstChild?.url[0]?.path;
      return this.account.features?.includes(FeatureFlag.ETHAN) && currentUrl !== 'ethan';
    }
  }

  get currentYear(): number {
    return new Date().getFullYear();
  }

  private accountSubdomain(value: string): boolean {
    return this.account?.subdomain === value;
  }

  lastUrl = signal<string>('');

  ngOnInit(): void {
    // Subscribe to window resize event
    this.renderer.listen('window', 'resize', () => {
      this.updateChatButtonPosition();
      if (this.chatButtonRef?.nativeElement) {
        this.snapToNearestEdge(this.chatButtonRef?.nativeElement);
      }
    });
    this.lastUrl.set(this._router.url);
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event: NavigationEnd) => {
        setTimeout(() => {
          this.setInitialPositionForChatButton();
          this.updateChatButtonPosition();
          this.ethanChatOpen$.pipe(take(1)).subscribe((isOpen) => {
            if (isOpen && this.activatedRoute.snapshot.firstChild?.url[0]?.path !== 'ethan') {
              this.openCloseChat();
            }
          });
        }, 1000);
        this.lastUrl.set(event.url);
      });

    this.fromAccounts = this.activatedRoute.snapshot.data.fromAccounts;

    if (this.fromAccounts) {
      return;
    }

    this.data = this.activatedRoute.snapshot.data.initialData;
    this.isTaskCollaborator = (this.data.user as User).roles.some((role) => role.name === ReservedRole.Collaborator);

    if (this.accountSubdomain('pepsico')) {
      this.PROPERTY_MENU_ITEM_TITLE = 'Buildings';
    }

    this.activatedRoute.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: Data) => {
      this.data = data.initialData;
    });

    this.store.dispatch(new ObjectsManagerActions.GetForMenu());

    this.customObjects$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((customObjects) => {
      const { filteredObjects, filteredObject } = this.filterCustomObjectsFromMoreDropdown(customObjects);
      this.customObjects = filteredObjects;
      this.initialMenuItems();
      this.generateMenuLinks();

      // [SC-47700] Push an specific custom object to the root menu as an option if it exists
      if (filteredObject) {
        this.pushCustomObjectToMenu(filteredObject);
      }
    });

    this.userService.recentProjects$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((projects: Project[]) => {
      this.projects = projects;
      this.updateRecentProjects();
    });

    this.userService.recentContacts$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((contacts: Contact[]) => {
      if (contacts) {
        this.contacts = contacts;
        this.updateRecentContacts();
      }
    });

    this.userService.recentCompanies$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((companies: Company[]) => {
      if (companies) {
        this.companies = companies;
        this.updateRecentContacts();
      }
    });

    this.userService.recentProperties$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((properties: Contact[]) => {
      if (properties) {
        this.updateRecentProperties(properties);
      }
    });

    this.projectsService.pipelines$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((pipelines) => {
      if (pipelines) {
        this.pipelines = pipelines.filter((pipeline) => pipeline?.pipeline_type != 'dynamic');
        this.updateRecentProjects();
      }
    });

    this.userService.favoriteRecords$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((favoriteRecords: any[]) => {
      this.favoriteRecords = favoriteRecords;
      this.updateRecentProjects();
    });

    if (this.account.id && !this.isNewNavigationOn) {
      this.userService.getRecentItems(this.account.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(noop);
      this.userService.getFavoriteRecords(this.account.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }

    this.treoMediaWatcherService.onMediaChange$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ matchingAliases }) => {
        // Check if the breakpoint is 'lt-xlg'
        // 'lt-xlg is a custom breakpoint to prevent desktop topmenu from generating a horizontal scrollbar.
        this.isScreenSmall = matchingAliases.includes('lt-xlg');
      });
  }

  ngAfterViewInit(): void {
    // Update button position after view initialization
    setTimeout(() => {
      this.updateChatButtonPosition();
    });
  }

  public openCloseChat(): void {
    if (!this.chatIsOpen && !this.isDragging) {
      let position = calculateDialogPosition(this.chatButtonPosition);

      if (this.canAccessEthan) {
        this.chatInstance = this.matDialog.open(FloatingEthanComponent, {
          width: '450px',
          height: '600px',
          position: position,
          maxWidth: 'unset',
          hasBackdrop: false,
          closeOnNavigation: true,
          panelClass: 'ethan-dialog',
          disableClose: true,
          scrollStrategy: new NoopScrollStrategy(),
        });
      }

      setTimeout(() => {
        this.chatInstance.addPanelClass('ethan-dialog-custom');
      }, 100);

      this.chatIsOpen = true;
      const backdrop = document.querySelector('.ethan-custom-backdrop');
      backdrop?.addEventListener('click', () => {
        this.chatInstance && this.chatInstance?.close();
      });

      this.chatInstance
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.chatIsOpen = false;
          this.changeDetectorRef.markForCheck();
        });
    } else {
      this.chatInstance && this.chatInstance?.close();
    }
    this.store.dispatch(new SetEthanChatOpen(false));
  }

  public onDragStart(event: MouseEvent, chatButton: HTMLElement): void {
    if (this.chatIsOpen) return;

    this.isDragging = true;
    this.hasMoved = false;
    const startX = event.clientX - this.chatButtonPosition.x;
    const startY = event.clientY - this.chatButtonPosition.y;

    const onMouseMove = (e: MouseEvent) => {
      if (!this.isDragging) return;

      this.hasMoved = true;
      this.chatButtonPosition.x = e.clientX - startX;
      this.chatButtonPosition.y = e.clientY - startY;

      chatButton.style.left = `${this.chatButtonPosition.x}px`;
      chatButton.style.bottom = `${window.innerHeight - this.chatButtonPosition.y}px`;
    };

    const onMouseUp = () => {
      if (this.hasMoved) {
        setTimeout(() => {
          this.isDragging = false;
          this.hasMoved = false;
          this.changeDetectorRef.markForCheck();
        }, 100);
      } else {
        this.isDragging = false;
        this.hasMoved = false;
      }

      this.snapToNearestEdge(chatButton);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  // Snap to nearest edge with padding
  private snapToNearestEdge(element: HTMLElement): void {
    const padding = 24;
    const rect = element.getBoundingClientRect();
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    // Calculate distances to edges
    const distanceToLeft = rect.left;
    const distanceToRight = windowWidth - (rect.left + rect.width);
    const distanceToTop = rect.top;
    const distanceToBottom = windowHeight - (rect.top + rect.height);

    // Find the nearest edge
    const distances = [
      { edge: 'left', distance: distanceToLeft },
      { edge: 'right', distance: distanceToRight },
      { edge: 'top', distance: distanceToTop },
      { edge: 'bottom', distance: distanceToBottom },
    ];

    const nearestEdge = distances.reduce((prev, curr) => (curr.distance < prev.distance ? curr : prev));

    // Snap to the nearest edge
    switch (nearestEdge.edge) {
      case 'left':
        this.chatButtonPosition.x = padding;
        break;
      case 'right':
        this.chatButtonPosition.x = windowWidth - padding - 50;
        break;
      case 'top':
        this.chatButtonPosition.y = padding + 50;
        break;
      case 'bottom':
        this.chatButtonPosition.y = windowHeight - padding;
        break;
    }

    element.style.left = `${this.chatButtonPosition.x}px`;
    element.style.bottom = `${window.innerHeight - this.chatButtonPosition.y}px`;

    this.saveChatButtonPosition();
  }

  private generateMenuLinks(): void {
    const customObjects = this.customObjects || [];

    if (!(customObjects.length && !this.isTaskCollaborator)) {
      return;
    }

    const customObjectsIds = customObjects.map(({ id }) => `custom_object_${id}`);
    const customObjectsAsNavItems = customObjects
      .map((customObj) => {
        if (
          this.account.subdomain === 'urbanedge' &&
          ['co_1195_prospective_tenants', 'co_1195_active_leases'].includes(customObj.name)
        ) {
          return null;
        }

        const permissions = PermissionUtils.generateCustomObjectPermissions(customObj.name);
        return {
          id: customObj.name,
          title: customObj.label_plural,
          link: `/space/${customObj.uiConfig.urlPrefix}`,
          type: 'basic',
          icon: this.objectIconMap[RelatedToType.CustomObject],
          exactMatch: true,
          classes: 'custom-object-nav-item',
          hidden: () => !this.hasPermission(permissions.VIEW),
          meta: {
            objectName: customObj.name,
            urlPrefix: customObj.uiConfig?.urlPrefix,
            urlMatches: [`/space/${customObj.uiConfig?.urlPrefix}`],
            rank: 9,
          },
        } as TreoNavigationItem;
      })
      .filter((item) => item != null);
    const customItems = this.getCustomOptionsForAccount(this.account.id.toString());
    const customItemIds = new Set(customItems.map((item) => item.id));
    const newItems = [];
    const customObjectsGroup: TreoNavigationItem = {
      id: guid(),
      title: 'More',
      type: 'collapsable',
      icon: this.objectIconMap[RelatedToType.CustomObject],
      iconClasses: 'text-white',
      meta: {
        showExpandIcon: true,
        isMoreItem: true,
        rank: 9,
      },
      children: customObjectsAsNavItems.filter((item) => !customItemIds.has(item.id)),
    };

    if (!this.isNewNavigationOn) {
      newItems.push(customObjectsGroup);
    } else {
      newItems.push(...customObjectsGroup.children);
    }

    this.menuItems = [
      ...this.menuItems.filter(({ id, meta }) => !customObjectsIds.includes(id) && !meta?.isMoreItem),
      ...customItems,
      ...newItems,
    ];
  }

  private updateRecentProjects(): void {
    let children: TreoNavigationItem[] = [
      {
        id: 'deals.all',
        title: 'All Views',
        link: '/projects',
        icon: this.objectIconMap[RelatedToType.Project],
        type: 'basic',
        exactMatch: true,
      },
    ];

    const index = this.menuItems.findIndex((item) => item.id === 'projects');
    if (this.pipelines) {
      let pipelineMenu: TreoNavigationItem = {
        id: 'pipelines',
        title: 'Views',
        icon: 'heroicons_outline:view-list',
        type: 'collapsable',
        children: [],
      };

      this.pipelines.forEach((pipeline) => {
        let pipelineChild: TreoNavigationItem = {
          id: pipeline.id.toString(),
          title: pipeline.name,
          type: 'collapsable',
          icon: 'heroicons_outline:view-list',
          children: [
            {
              id: 'deals-' + pipeline.id.toString(),
              title: `View Pipeline ${this.DEAL_MENU_ITEM_TITLE}`,
              type: 'basic',
              icon: this.objectIconMap[RelatedToType.Project],
              link: `/projects/${pipeline.id}`,
              exactMatch: true,
            },
          ],
        };

        if (this.projects) {
          let pipelineDeals = this.projects.filter((proj) => proj.pipeline_id == pipeline.id);

          pipelineDeals.forEach((project) => {
            let child: TreoNavigationItem = {
              id: project.id.toString(),
              title: project.name,
              type: 'basic',
              icon: this.objectIconMap[RelatedToType.Project],
              link: `/projects/${project.pipeline_id}/${project.id}`,
              exactMatch: true,
            };
            pipelineChild.children.push(child);
          });
          pipelineMenu.children.push(pipelineChild);
        }
      });
      children.push(pipelineMenu);
    }

    if (this.projects) {
      let recentProjectsMenu: TreoNavigationItem = {
        id: 'projects',
        title: `Recent ${this.DEAL_MENU_ITEM_TITLE}`,
        icon: 'heroicons_outline:clock',
        type: 'collapsable',
        children: [],
      };
      this.projects.forEach((project: Project & { name: string; url: string }) => {
        let child: TreoNavigationItem = {
          id: project.id.toString(),
          title: project.name,
          type: 'basic',
          icon: 'heroicons_outline:clock',
          link: project.url.replace('https://', '').split('/').slice(1).join('/'),
          exactMatch: true,
        };
        recentProjectsMenu.children.push(child);
      });
      children.push(recentProjectsMenu);
    }

    if (this.favoriteRecords) {
      let favoriteProjects = this.favoriteRecords.filter((item) => item.related_to_type == 'Project');
      let favoriteProjectsMenu: TreoNavigationItem = {
        id: 'projects',
        title: 'Favorites',
        icon: 'heroicons_outline:star',
        type: 'collapsable',
        children: [],
      };
      favoriteProjects.forEach((fave) => {
        let project = fave.favorite_object;
        if (project) {
          let child: TreoNavigationItem = {
            id: project.id.toString(),
            title: project.title,
            type: 'basic',
            icon: 'heroicons_outline:star',
            link: `/projects/${project.pipeline_id}/${project.id}`,
            exactMatch: true,
          };
          favoriteProjectsMenu.children.push(child);
        }
      });
      children.push(favoriteProjectsMenu);
    }
    if (index > -1) {
      let menuItem = this.menuItems[index];
      menuItem.children = children;
      this.menuItems[index] = menuItem;
    }
  }

  private updateRecentContacts(): void {
    const canShowCompanies = () =>
      !this.authService.hasAnyPermission(
        NewPermission.COMPANIES_VIEW,
        NewPermission.COMPANIES_CREATE,
        NewPermission.COMPANIES_EDIT,
        NewPermission.COMPANIES_DELETE,
        NewPermission.COMPANIES_MANAGE_ADD_FORM,
        NewPermission.COMPANIES_MANAGE_LAYOUT,
        NewPermission.COMPANIES_PUBLIC_CUSTOM_VIEWS,
        NewPermission.COMPANIES_SHARING_RECORDS,
      );

    const index = this.menuItems.findIndex((item) => item.id === 'contacts');
    let children: TreoNavigationItem[] = [
      {
        id: 'companies.all',
        title: 'Companies',
        link: '/space/companies',
        icon: this.objectIconMap[RelatedToType.Company],
        type: 'basic',
        exactMatch: true,
        hidden: canShowCompanies,
      },
    ];

    let recentCompanies: TreoNavigationItem = {
      id: 'companies.recent',
      title: 'Recent Companies',
      icon: 'heroicons_outline:clock',
      type: 'collapsable',
      children: [],
      hidden: canShowCompanies,
    };

    if (index > -1 && this.companies) {
      let menuItem = this.menuItems[index];
      if (menuItem) {
        this.companies.forEach((company: Company & { url: string }) => {
          let child: TreoNavigationItem = {
            id: company.id.toString(),
            title: `${company.name}`,
            type: 'basic',
            icon: 'heroicons_outline:user',
            meta: { entity: company },
            exactMatch: true,
            function: (item) => {
              this._router.navigateByUrl(`space/void?return=/space/companies/0/${item.id}`);
            },
          };
          recentCompanies.children.push(child);
        });
        children.push(recentCompanies);
        menuItem.children = children;
        this.menuItems[index] = menuItem;
      }
    }

    const canShowContacts = () =>
      !this.authService.hasAnyPermission(
        NewPermission.CONTACTS_CREATE,
        NewPermission.CONTACTS_EDIT,
        NewPermission.CONTACTS_VIEW,
        NewPermission.CONTACTS_DELETE,
        NewPermission.CONTACTS_MANAGE_ADD_FORM,
        NewPermission.CONTACTS_MANAGE_LAYOUT,
        NewPermission.CONTACTS_SHARING_RECORDS,
      );

    children.push({
      id: 'contacts.all',
      title: 'Contacts',
      link: '/space/contacts',
      icon: 'heroicons_outline:users',
      type: 'basic',
      exactMatch: true,
      hidden: canShowContacts,
    });

    let recentContacts: TreoNavigationItem = {
      id: 'contacts.recent',
      title: 'Recent Contacts',
      icon: 'heroicons_outline:clock',
      type: 'collapsable',
      children: [],
      hidden: canShowContacts,
    };

    if (index > -1 && this.contacts) {
      let menuItem = this.menuItems[index];
      if (menuItem) {
        this.contacts.forEach((contact: Contact & { url: string }) => {
          let child: TreoNavigationItem = {
            id: contact.id.toString(),
            title: `${contact.name}`,
            type: 'basic',
            icon: 'heroicons_outline:user',
            meta: { entity: contact },
            exactMatch: true,
            function: (item) => {
              this._router.navigateByUrl(`space/void?return=/space/contacts/0/${item.id}`);
            },
          };
          recentContacts.children.push(child);
        });
        children.push(recentContacts);
        menuItem.children = children;
        this.menuItems[index] = menuItem;
      }
    }
  }

  private updateRecentProperties(properties): void {
    const index = this.menuItems.findIndex((item) => item.id === 'properties');

    let children: TreoNavigationItem[] = [
      {
        id: 'properties.all',
        title: `All ${this.PROPERTY_MENU_ITEM_TITLE}`,
        link: '/space/properties',
        icon: this.objectIconMap[RelatedToType.Property],
        type: 'basic',
        exactMatch: true,
      },
    ];

    if (index > -1 && properties) {
      let menuItem = this.menuItems[index];
      if (menuItem) {
        properties.forEach((property: Property & { url: string }) => {
          let child: TreoNavigationItem = {
            id: property.id.toString(),
            title: `${property?.name || property?.address_string || 'Untitled'}`,
            type: 'basic',
            icon: 'heroicons_outline:clock',
            meta: { entity: property },
            exactMatch: true,
            function: (item) => {
              this._router.navigateByUrl(`space/void?return=/space/properties/0/${item.id}`);
            },
          };
          children.push(child);
        });
        menuItem.children = children;
        this.menuItems[index] = menuItem;
      }
    }
  }

  public toggleNavigation(key: string): void {
    const navigation = this.treoNavigationService.getComponent(key);

    if (navigation) {
      navigation.toggle();
    }
  }

  public toggleFloatingEthan(): void {
    this.isFloatingEthanVisible = !this.isFloatingEthanVisible;
    localStorage.setItem(this.FLOATING_ETHAN_KEY, this.isFloatingEthanVisible.toString());

    // Set the initial position
    this.chatButtonPosition = { x: 24, y: window.innerHeight - 104 };
    localStorage.setItem(this.CHAT_POSITION_KEY, JSON.stringify(this.chatButtonPosition));

    // Update the button position
    if (this.chatButtonRef?.nativeElement) {
      this.renderer.setStyle(this.chatButtonRef?.nativeElement, 'left', `${this.chatButtonPosition.x}px`);
      this.renderer.setStyle(
        this.chatButtonRef?.nativeElement,
        'bottom',
        `${window.innerHeight - this.chatButtonPosition.y}px`,
      );
    }
    this.chatInstance?.close();

    this.changeDetectorRef.detectChanges();
  }

  private initialMenuItems(): void {
    this.menuItems = [];

    if (this.authService.isGuest()) {
      this.menuItems = [
        {
          id: 'projects',
          title: this.DEAL_MENU_ITEM_TITLE,
          icon: this.objectIconMap[RelatedToType.Project],
          iconClasses: 'text-white',
          type: 'collapsable',
        },
      ];

      return;
    }

    if (this.isTaskCollaborator) {
      this.menuItems = [
        {
          id: 'tasks',
          title: 'Tasks',
          link: '/tasks',
          icon: this.objectIconMap[RelatedToType.Task],
          iconClasses: 'text-white',
          type: 'collapsable',
          meta: {
            showExpandIcon: true,
          },
          children: [
            {
              id: 'tasks.global',
              title: 'Global',
              link: '/tasks/global',
              icon: this.objectIconMap[RelatedToType.Task],
              type: 'basic',
              exactMatch: false,
            },
          ],
        },
      ];

      return;
    }

    const projectsMenuItem: TreoNavigationItem = {
      id: 'projects',
      title: this.DEAL_MENU_ITEM_TITLE,
      link: '/projects',
      icon: this.objectIconMap[RelatedToType.Project],
      iconClasses: 'text-white',
      type: 'collapsable',
      meta: {
        showExpandIcon: true,
        urlMatches: ['/projects'],
        rank: 1,
      },
      hidden: () => !this.authService.hasAnyPermissionInModule(Module.DEALS),
    };

    const contactsMenuItem: TreoNavigationItem = {
      id: 'contacts',
      title: 'CRM',
      link: '/space/contacts',
      icon: this.objectIconMap[RelatedToType.Contact],
      iconClasses: 'text-white',
      type: 'collapsable',
      meta: {
        showExpandIcon: true,
        urlMatches: ['/space/contacts'],
        urlPrefix: this.store.selectSnapshot(ObjectsManagerState.getByName(RelatedToType.Contact)).uiConfig?.urlPrefix,
        rank: 4,
      },
      hidden: () => !this.authService.hasAnyPermissionInModule(Module.CONTACTS),
    };

    const tasksMenuItem: TreoNavigationItem = {
      id: 'tasks',
      title: 'Tasks',
      link: this.isNewNavigationOn ? `/new-tasks/${RelatedToType.AggregatedTasks}/${this.account.id}/` : '/tasks',
      icon: this.objectIconMap[RelatedToType.Task],
      iconClasses: 'text-white',
      type: 'collapsable',
      meta: {
        showExpandIcon: true,
        urlMatches: ['/new-tasks'],
        rank: 6,
      },
      hidden: () => !this.authService.hasAnyPermissionInModule(Module.TASKS),
      children: [
        {
          id: 'tasks.new.global',
          title: 'Global Tasks',
          link: `/new-tasks/${RelatedToType.Account}/${this.account.id}/`,
          icon: this.objectIconMap[RelatedToType.Task],
          type: 'basic',
          exactMatch: true,
          meta: {
            rank: 6,
          },
          hidden: () => !this.authService.hasAnyPermissionInModule(Module.GLOBAL_TASKS),
        },
        {
          id: 'tasks.new.aggregated',
          title: 'Aggregated Tasks',
          link: `/new-tasks/${RelatedToType.AggregatedTasks}/${this.account.id}/`,
          icon: this.objectIconMap[RelatedToType.Task],
          type: 'basic',
          exactMatch: true,
          meta: {
            rank: 6,
          },
          hidden: () => !this.authService.hasAnyPermissionInModule(Module.AGGREGATED_TASKS),
        },
      ],
    };

    const propertiesMenuItem: TreoNavigationItem = {
      id: 'properties',
      title: this.PROPERTY_MENU_ITEM_TITLE,
      link: '/space/properties',
      icon: this.objectIconMap[RelatedToType.Property],
      iconClasses: 'text-white',
      type: 'collapsable',
      meta: {
        showExpandIcon: true,
        urlMatches: ['/space/properties'],
        urlPrefix: this.store.selectSnapshot(ObjectsManagerState.getByName(RelatedToType.Property)).uiConfig?.urlPrefix,
        rank: 2,
      },
      exactMatch: false,
      hidden: () => !this.authService.hasAnyPermissionInModule(Module.PROPERTIES),
    };

    const leasesMenuItem: TreoNavigationItem = {
      id: 'leases',
      title: 'Leases',
      link: '/space/leases',
      icon: this.objectIconMap[RelatedToType.PropertyTenant],
      iconClasses: 'text-white',
      type: 'basic',
      hidden: () => !this.authService.hasAnyPermissionInModule(Module.PROPERTIES),
      meta: {
        urlMatches: ['/space/leases'],
        urlPrefix: this.store.selectSnapshot(ObjectsManagerState.getByName(RelatedToType.PropertyTenant)).uiConfig
          ?.urlPrefix,
        rank: 3,
      },
    };

    const researchMenuItem: TreoNavigationItem = {
      id: 'research',
      title: 'Maps',
      link: '/research/currentLocation',
      icon: 'heroicons_outline:globe',
      iconClasses: 'text-white',
      type: 'basic',
      meta: {
        useRouterLink: true,
        urlMatches: ['/research/currentLocation'],
        rank: 8,
      },
    };

    const reportingMenuItem: TreoNavigationItem = {
      id: 'reporting',
      title: 'Reports',
      link: '/reporting',
      icon: this.isNewNavigationOn ? 'chart-line' : 'heroicons_outline:document-report',
      iconClasses: 'text-white',
      type: 'collapsable',
      meta: {
        showExpandIcon: true,
        urlMatches: ['/reporting', '/dashboard', '/reports'],
        rank: 7,
      },
      hidden: () =>
        !this.authService.hasAnyPermissionInModule(Module.REPORTS) &&
        !this.authService.hasAnyPermissionInModule(Module.DASHBOARDS),
      children: [
        {
          id: 'reports.dashboard',
          title: 'Dashboard',
          link: '/dashboard',
          icon: 'ts_create-dashboard',
          type: 'basic',
          hidden: () =>
            !this.authService.hasAnyPermission(
              NewPermission.DASHBOARDS_EDIT,
              NewPermission.DASHBOARDS_VIEW,
              NewPermission.DASHBOARDS_DELETE,
              NewPermission.DASHBOARDS_CREATE,
              NewPermission.DASHBOARDS_SHARING_RECORDS,
            ),
        },
        {
          id: 'reports.builder',
          title: 'Report Builder',
          link: '/reporting/list',
          icon: 'ts_graph-bar',
          type: 'basic',
          exactMatch: true,
          hidden: () =>
            !this.authService.hasAnyPermission(
              NewPermission.REPORTS_BUILDER_DELETE,
              NewPermission.REPORT_BUILDER_SHARING_RECORDS,
              NewPermission.REPORT_BUILDER_CREATE,
              NewPermission.REPORTS_BUILDER_VIEW,
              NewPermission.REPORTS_BUILDER_EDIT,
            ),
        },
        {
          id: 'reports.pdf',
          title: 'PDF Reports',
          link: '/reports',
          icon: 'ts_file-info-alt',
          type: 'basic',
          exactMatch: false,
          hidden: () =>
            !this.authService.hasAnyPermission(
              NewPermission.PDF_REPORTS_CREATE,
              NewPermission.PDF_REPORTS_DELETE,
              NewPermission.PDF_REPORTS_EDIT,
              NewPermission.PDF_REPORTS_SHARING_RECORDS,
              NewPermission.PDF_REPORTS_VIEW,
            ) || !this.hasPermission(NewPermission.DEALS_VIEW),
        },
      ],
    };

    if (this.account?.subdomain === 'urbanedge') {
      const prospectiveTenantMenuItem: TreoNavigationItem = {
        id: `custom_object_prospective_tenants`,
        title: 'Prospective Tenants',
        link: `/space/prospective-tenants`,
        type: 'basic',
        icon: 'heroicons_outline:users',
        iconClasses: 'text-white',
        hidden: () =>
          !this.hasPermission(PermissionUtils.generateCustomObjectPermissions('co_1195_prospective_tenants').VIEW),
      };

      this.menuItems = [
        propertiesMenuItem,
        prospectiveTenantMenuItem,
        contactsMenuItem,
        tasksMenuItem,
        researchMenuItem,
        reportingMenuItem,
      ];
    } else {
      if (!this.isNewNavigationOn) {
        this.menuItems = [
          projectsMenuItem,
          contactsMenuItem,
          tasksMenuItem,
          propertiesMenuItem,
          leasesMenuItem,
          researchMenuItem,
          reportingMenuItem,
        ];
      } else {
        const companiesMenuItem: TreoNavigationItem = {
          id: 'companies',
          title: 'Companies',
          link: '/space/companies',
          icon: 'heroicons_outline:user-group',
          iconClasses: 'text-white',
          type: 'collapsable',
          meta: {
            showExpandIcon: true,
            urlPrefix: this.store.selectSnapshot(ObjectsManagerState.getByName(RelatedToType.Company)).uiConfig
              ?.urlPrefix,
            urlMatches: ['/space/companies'],
            rank: 5,
          },
          hidden: () => !this.authService.hasAnyPermissionInModule(Module.CONTACTS),
        };

        this.menuItems = [
          projectsMenuItem,
          { ...contactsMenuItem, title: 'Contacts' },
          companiesMenuItem,
          tasksMenuItem,
          propertiesMenuItem,
          leasesMenuItem,
          researchMenuItem,
          reportingMenuItem,
        ];
      }
    }
  }

  private hasPermission(permission: NewPermission | string): boolean {
    return this.store.selectSnapshot(UserState.hasPermission(permission));
  }

  private updateChatButtonPosition(): void {
    if (this.chatButtonRef?.nativeElement) {
      // Ensure the button stays within window bounds
      const maxX = window.innerWidth - this.chatButtonRef?.nativeElement.offsetWidth;
      const maxY = window.innerHeight - this.chatButtonRef?.nativeElement.offsetHeight;

      // Clamp the position to window bounds
      const clampedX = Math.min(Math.max(0, this.chatButtonPosition.x), maxX);
      const clampedY = Math.min(Math.max(0, this.chatButtonPosition.y), maxY);
      this.chatButtonPosition = { x: clampedX, y: clampedY };

      this.renderer.setStyle(this.chatButtonRef?.nativeElement, 'left', `${clampedX}px`);
      this.renderer.setStyle(this.chatButtonRef?.nativeElement, 'bottom', `${window.innerHeight - clampedY}px`);
    }
    setTimeout(() => {
      if (!this._router.url.includes('ethan')) {
        this.store.dispatch(new SetEthanButtonVisible(true));
      }
    }, 100);
  }

  private saveChatButtonPosition(): void {
    localStorage.setItem(this.CHAT_POSITION_KEY, JSON.stringify(this.chatButtonPosition));
  }

  private getCustomOptionsForAccount(accountId: string): TreoNavigationItem[] {
    switch (accountId) {
      case '1492':
        const pepsiLeasesObj = this.customObjects.find((customObj) => customObj.id === 1047);
        if (!pepsiLeasesObj) {
          return [];
        }
        const permissions = PermissionUtils.generateCustomObjectPermissions(pepsiLeasesObj.name);
        return [
          {
            id: 'custom_object_1047',
            title: pepsiLeasesObj.label_plural,
            link: `/space/${pepsiLeasesObj.uiConfig.urlPrefix}`,
            icon: 'ts_shop',
            iconClasses: 'text-white',
            type: 'basic',
            hidden: () => !this.hasPermission(permissions.VIEW),
            meta: {
              rank: 9,
            },
          },
        ];
      default:
        return [];
    }
  }

  /**
   * [SC-47700] Filter out a specific custom object by its name from menu 'More' dropdown options based on different subdomains.
   * @param customObjects - The custom objects to filter.
   * @returns The filtered custom objects.
   */
  private filterCustomObjectsFromMoreDropdown(customObjects: CustomObject[]): {
    filteredObjects: CustomObject[];
    filteredObject?: CustomObject;
  } {
    if (this.accountSubdomain('teamsentral')) {
      return this.handleMenuOptionsForTeamSentral(customObjects);
    }

    return { filteredObjects: customObjects };
  }

  /**
   * [SC-47700] Filter out the Sentral Communities custom object for teamsentral subdomain.
   * @param customObjects - The custom objects to filter.
   * @returns Object containing filtered objects and the filtered out object.
   */
  private handleMenuOptionsForTeamSentral(customObjects: CustomObject[]): {
    filteredObjects: CustomObject[];
    filteredObject?: CustomObject;
  } {
    const customObjRootName = 'co_1534_sentral_communities';
    const filteredObject = customObjects.find((customObj) => customObj.name === customObjRootName);
    const filteredObjects = customObjects.filter((customObj) => customObj.name !== customObjRootName);
    return { filteredObjects, filteredObject };
  }

  /**
   * [SC-47700] Push an specific custom object to the menu options.
   * @param customObject - The custom object to push to the menu.
   */
  private pushCustomObjectToMenu(customObject: CustomObject): void {
    this.menuItems.unshift({
      id: `custom_object_${customObject.id}`,
      title: customObject.label_plural,
      link: `/space/${customObject.uiConfig.urlPrefix}`,
      type: 'basic',
      icon: 'heroicons_outline:qrcode',
      iconClasses: 'text-white',
      classes: 'custom-object-nav-item',
      hidden: () => !this.hasPermission(PermissionUtils.generateCustomObjectPermissions(customObject.name).VIEW),
    });
  }
}
